import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, Typography, CircularProgress,
  Box, Alert, MenuItem, FormControl, InputLabel, Select,
  Stepper, Step, StepLabel
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const CourseGenerationSchema = Yup.object().shape({
  name: Yup.string().required('Course name is required'),
  description: Yup.string().required('Description is required').min(20, 'Please provide at least 20 characters for better AI generation'),
  model: Yup.string().required('Model selection is required')
});

// Available Gemini model options with user-friendly display names
const GEMINI_MODEL_OPTIONS = [
  { value: 'gemini-1.5-pro-001', label: 'Gemini 1.5 Pro' },
  { value: 'gemini-1.5-flash-001', label: 'Gemini 1.5 Flash (Faster)' },
  { value: 'gemini-1.5-ultra-001', label: 'Gemini 1.5 Ultra (Most powerful)' },
  { value: 'gemini-2.0-flash-001', label: 'Gemini 2.0 Flash' },
  { value: 'gemini-2.5-flash-preview-04-17', label: 'Gemini 2.5 Flash Preview (Fastest)' },
  { value: 'gemini-2.5-pro-exp-03-25', label: 'Gemini 2.5 Pro (Experimental)' },
  { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro Preview' },
  { value: 'gemini-3.1-flash', label: 'Gemini 3.1 Flash' },
  { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
];

// Other model options
const OTHER_MODEL_OPTIONS = [
  { value: 'gpt-4', label: 'OpenAI GPT-4' },
  { value: 'gpt-3.5-turbo', label: 'OpenAI GPT-3.5 Turbo' },
  { value: 'claude-3', label: 'Anthropic Claude 3' },
];

export default function CourseGenerationDialog({ 
  open, 
  onClose, 
  onSubmit,
  isGenerating
}) {
  const [jobSubmitted, setJobSubmitted] = useState(false);
  const [jobId, setJobId] = useState(null);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setJobSubmitted(false);
      setJobId(null);
    }
  }, [open]);

  const handleSubmitWrapper = async (values, formikBag) => {
    try {
      const result = await onSubmit(values, formikBag);
      if (result && result.jobId) {
        setJobId(result.jobId);
        setJobSubmitted(true);
      }
    } catch (error) {
      console.error('Error in job submission:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Complete Course with AI</DialogTitle>
      <Formik
        initialValues={{
          name: '',
          description: '',
          model: 'gemini-3.1-pro'  // Updated default to Gemini 3.1 Pro
        }}
        validationSchema={CourseGenerationSchema}
        onSubmit={handleSubmitWrapper}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <DialogContent>
              {!jobSubmitted ? (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    This will start an asynchronous job to generate a complete course with multiple themes and content blocks
                    at various difficulty levels. The job may take several minutes to complete.
                  </Alert>

                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Course Name:
                    </Typography>
                    <Field name="name">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="E.g., Introduction to JavaScript Programming"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Course Description:
                    </Typography>
                    <Field name="description">
                      {({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={5}
                          placeholder="Provide a detailed description of the course content, target audience, and learning objectives. More details will result in better AI generation."
                          error={touched.description && Boolean(errors.description)}
                          helperText={touched.description && errors.description}
                        />
                      )}
                    </Field>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      A more detailed description helps the AI create better content.
                    </Typography>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      AI Model:
                    </Typography>
                    <Field name="model">
                      {({ field }) => (
                        <FormControl fullWidth error={touched.model && Boolean(errors.model)}>
                          <InputLabel>Select AI Model</InputLabel>
                          <Select
                            {...field}
                            label="Select AI Model"
                          >
                            <MenuItem disabled sx={{ opacity: 0.7 }}>
                              <Typography fontWeight="bold">
                                Gemini Models
                              </Typography>
                            </MenuItem>
                            {GEMINI_MODEL_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                            
                            <MenuItem disabled sx={{ opacity: 0.7, mt: 1 }}>
                              <Typography fontWeight="bold">
                                Other Models
                              </Typography>
                            </MenuItem>
                            {OTHER_MODEL_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.model && errors.model && (
                            <Typography color="error" variant="caption">{errors.model}</Typography>
                          )}
                        </FormControl>
                      )}
                    </Field>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Gemini 3.1 Pro offers a good balance of quality and speed. Ultra models provide highest quality results but take longer.
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box textAlign="center" py={3}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Job submitted successfully! Your course is being generated.
                  </Alert>
                  
                  <Stepper activeStep={1} alternativeLabel sx={{ mb: 4 }}>
                    <Step completed>
                      <StepLabel>Job Submitted</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>Generating Content</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>Course Ready</StepLabel>
                    </Step>
                  </Stepper>
                  
                  {jobId && (
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Job ID: {jobId}
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography paragraph>
                    This job is being processed in the background. You can close this dialog and check back later.
                    The course will appear in your course list when generation is complete.
                  </Typography>
                </Box>
              )}

              {isGenerating && !jobSubmitted && (
                <Box display="flex" justifyContent="center" my={4} flexDirection="column" alignItems="center">
                  <CircularProgress size={50} />
                  <Typography mt={2}>Starting course generation job...</Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Please wait while we submit your request.
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isGenerating && !jobSubmitted}>
                {jobSubmitted ? 'Close' : 'Cancel'}
              </Button>
              {!jobSubmitted && (
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={isGenerating || isSubmitting}
                  startIcon={isGenerating && <CircularProgress size={20} color="inherit" />}
                >
                  {isGenerating ? 'Starting Job...' : 'Generate Course'}
                </Button>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
